import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { api, fmtDate, ukToday } from "@/admin/api";
import { Btn, Card, Empty, ErrorMsg, Field, inputCls, Loading, Modal, useAsync } from "@/admin/ui";

const pad = (n) => String(n).padStart(2, "0");
const ymOf = (ymd) => ymd.slice(0, 7);
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const BookingForm = ({ date, jobId, job, onClose, onSaved }) => {
  const [f, setF] = useState({ name: job?.name || "", phone: job?.phone || "", address: job?.address || "", work_type: job?.work_type || "", kind: "site_visit", date, time: "", notes: "" });
  const [error, setError] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      onSaved(await api("/admin/bookings", { method: "POST", body: { ...f, job_id: jobId || undefined } }));
    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <Modal title="Add booking" onClose={onClose} testId="booking-modal">
      <form onSubmit={save} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select data-testid="booking-kind" value={f.kind} onChange={set("kind")} className={inputCls}><option value="site_visit">Site visit</option><option value="job">Job date</option></select>
          </Field>
          <Field label="Time"><input data-testid="booking-time" type="time" value={f.time} onChange={set("time")} className={inputCls} /></Field>
        </div>
        <Field label="Date"><input data-testid="booking-date" type="date" required value={f.date} onChange={set("date")} className={inputCls} /></Field>
        <Field label="Name"><input data-testid="booking-name" required value={f.name} onChange={set("name")} className={inputCls} disabled={Boolean(job)} /></Field>
        <Field label="Phone"><input data-testid="booking-phone" type="tel" value={f.phone} onChange={set("phone")} className={inputCls} disabled={Boolean(job)} /></Field>
        <Field label="Address"><input data-testid="booking-address" value={f.address} onChange={set("address")} className={inputCls} /></Field>
        <Field label="Type of work"><input data-testid="booking-work" value={f.work_type} onChange={set("work_type")} className={inputCls} disabled={Boolean(job)} placeholder="e.g. Rear extension" /></Field>
        <Field label="Notes"><textarea data-testid="booking-notes" rows={3} value={f.notes} onChange={set("notes")} className={`${inputCls} py-3`} /></Field>
        {!jobId && <p className="text-xs text-body">A client &amp; job record is created automatically from these details.</p>}
        <ErrorMsg error={error} />
        <Btn type="submit" data-testid="booking-save" className="w-full">Save booking</Btn>
      </form>
    </Modal>
  );
};

export default function Calendar() {
  const today = ukToday();
  const [month, setMonth] = useState(ymOf(today));
  const [selected, setSelected] = useState(today);
  const [adding, setAdding] = useState(false);
  const { data, error, loading, reload } = useAsync(() => api(`/admin/bookings?month=${month}`), [month]);

  const [y, m] = month.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const offset = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => `${y}-${pad(m)}-${pad(i + 1)}`)];
  const shift = (d) => {
    const nm = new Date(Date.UTC(y, m - 1 + d, 1));
    setMonth(`${nm.getUTCFullYear()}-${pad(nm.getUTCMonth() + 1)}`);
  };
  const byDay = (d) => (data || []).filter((b) => b.date === d);
  const dayList = byDay(selected);

  return (
    <div data-testid="calendar-page" className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-navy md:text-3xl">Calendar</h1>
        <Btn onClick={() => setAdding(true)} data-testid="add-booking-button" className="min-h-11 px-4"><Plus size={18} /> Add booking</Btn>
      </div>
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <button type="button" aria-label="Previous month" data-testid="prev-month" onClick={() => shift(-1)} className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-alt"><ChevronLeft /></button>
          <p data-testid="calendar-month-label" className="text-lg font-bold text-navy">{first.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" })}</p>
          <button type="button" aria-label="Next month" data-testid="next-month" onClick={() => shift(1)} className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-alt"><ChevronRight /></button>
        </div>
        <div className="grid grid-cols-7 text-center text-xs font-semibold text-body">{DOW.map((d) => <div key={d} className="py-1">{d}</div>)}</div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => d ? (
            <button key={d} type="button" data-testid={`day-${d}`} onClick={() => setSelected(d)} aria-pressed={selected === d}
              className={`flex min-h-12 flex-col items-center justify-center rounded-xl text-sm font-semibold ${selected === d ? "bg-brand text-white" : d === today ? "bg-alt text-brand" : "text-navy hover:bg-alt"}`}>
              {Number(d.slice(-2))}
              <span className="mt-0.5 flex h-1.5 gap-0.5">{byDay(d).slice(0, 3).map((b) => <span key={b.id} className={`h-1.5 w-1.5 rounded-full ${selected === d ? "bg-white" : "bg-brand"}`} />)}</span>
            </button>
          ) : <div key={`e${i}`} />)}
        </div>
      </Card>
      {loading && <Loading />}
      <ErrorMsg error={error} />
      <section>
        <h2 data-testid="selected-day-heading" className="mb-3 text-lg font-bold text-navy">{fmtDate(selected)}</h2>
        {dayList.length === 0 ? <Empty testId="day-empty">No bookings on this day.</Empty> : (
          <ul className="space-y-3">
            {dayList.map((b) => (
              <li key={b.id}>
                <Link to={`/admin/jobs/${b.job_id}`} data-testid={`booking-${b.id}`} className="block">
                  <Card className="hover:border-brand">
                    <p className="font-semibold text-navy">{b.time ? `${b.time} · ` : ""}{b.name}</p>
                    <p className="text-sm text-body">{b.kind === "site_visit" ? "Site visit" : "Job"}{b.work_type ? ` · ${b.work_type}` : ""}{b.address ? ` · ${b.address}` : ""}</p>
                    {b.notes && <p className="mt-1 text-sm text-body">{b.notes}</p>}
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
      {adding && <BookingForm date={selected} onClose={() => setAdding(false)} onSaved={(b) => { setAdding(false); setMonth(ymOf(b.date)); setSelected(b.date); reload(); }} />}
    </div>
  );
}
